// Rechnungen direkt im Tool: Aufbau wie in MILKEE (Positionen, Textzeilen, Gruppen). Gespeichert wird in MILKEE.

export interface InvPos {
  type: "position";
  description: string;
  amount: number;
  unit: string;
  price: number;
  /** Rabatt auf die Position (Betrag oder Prozent) */
  discount: number;
  discountType: "absolute" | "percentage";
}
export interface InvText {
  type: "text";
  text: string;
}
export interface InvGroup {
  type: "collection";
  title: string;
  positions: InvPos[];
}
export type InvLine = InvPos | InvText | InvGroup;

/** erfasste Zeit, die mit der Rechnung verrechnet wird (eigener Abschnitt «Zeiten» wie in MILKEE) */
export interface InvTime {
  id: number;
  date: string;
  project: string;
  description: string;
  hours: number;
  minutes: number;
  rate: number;
  total: number;
}
/** offene, weiterverrechenbare Auslage (Buchung) */
export interface InvExpense {
  id: number;
  date: string;
  description: string;
  sum: number;
}
/** Zeiten auf der Rechnung: wie in MILKEE nicht gruppiert, nach Projekt oder nach Datum */
export const TIME_GROUPS = ["none", "project", "date"] as const;
export const TIME_ORDERS = ["desc", "asc"] as const;

export const LANGS = ["de", "en", "fr", "it"] as const;
export const CURRENCIES = ["CHF", "EUR", "USD", "GBP"] as const;

export interface InvoiceInput {
  customerId: number;
  projectId: number | null;
  title: string;
  date: string;
  payableUntil: string;
  /** leer = nächste freie Nummer aus MILKEE */
  number: string;
  lang: (typeof LANGS)[number];
  currency: (typeof CURRENCIES)[number];
  taxRateId: number | null;
  /** Gesamtrabatt in Prozent */
  discountRate: number;
  remarksTop: string;
  remarks: string;
  lines: InvLine[];
  /** erfasste Zeiten, die mit dieser Rechnung verrechnet werden */
  timeIds: number[];
  /** weiterverrechnete Auslagen */
  entryIds: number[];
  groupBy: (typeof TIME_GROUPS)[number];
  /** desc = neueste zuerst */
  timesOrder: (typeof TIME_ORDERS)[number];
}

export interface InvoiceFull extends InvoiceInput {
  id: number;
  status: string;
  customerName: string;
  overdue: boolean;
  totals: { net: number; discount: number; vat: number; total: number };
  activities: { date: string; message: string }[];
  times: InvTime[];
  expenses: InvExpense[];
}

export interface InvoiceMeta {
  nextNumber: string;
  taxRates: { id: number; name: string; rate: number }[];
  products: { id: number; name: string; description: string; price: number; unit: string }[];
}

/** Positionssumme nach Rabatt */
export const posSum = (p: InvPos) => {
  const gross = (Number(p.amount) || 0) * (Number(p.price) || 0);
  const d = Number(p.discount) || 0;
  return Math.round((p.discountType === "percentage" ? gross * (1 - d / 100) : gross - d) * 100) / 100;
};

/** Zwischentotal aller Positionen (auch in Gruppen) */
export const linesNet = (lines: InvLine[]) =>
  Math.round(lines.reduce((a, l) => a + (l.type === "position" ? posSum(l) : l.type === "collection" ? l.positions.reduce((x, p) => x + posSum(p), 0) : 0), 0) * 100) / 100;

/** Status in MILKEE → Anzeige */
export const INVOICE_STATUS: Record<string, string> = {
  open: "Entwurf",
  sent: "Versendet",
  paid: "Bezahlt",
  reminder_one: "1. Mahnung",
  reminder_two: "2. Mahnung",
  reminder_three: "3. Mahnung",
};

export const timeHours = (t: Pick<InvTime, "hours" | "minutes">) => Math.round((t.hours + t.minutes / 60) * 100) / 100;
export const timesSum = (ts: InvTime[]) => Math.round(ts.reduce((a, t) => a + t.total, 0) * 100) / 100;
export const expensesSum = (es: InvExpense[]) => Math.round(es.reduce((a, e) => a + e.sum, 0) * 100) / 100;
/** Was beim nächsten Rechnungschreiben dazukäme: offene Zeiten und Auslagen eines Kunden */
export function openWork(src: { times: InvTime[]; expenses: InvExpense[] }) {
  const expenses = expensesSum(src.expenses);
  return {
    minutes: src.times.reduce((a, t) => a + t.hours * 60 + t.minutes, 0),
    count: src.times.length,
    expenses,
    money: Math.round((timesSum(src.times) + expenses) * 100) / 100,
  };
}

/** Zeiten so zusammenfassen, wie MILKEE sie auf der Rechnung zeigt */
export function groupTimes(ts: InvTime[], by: InvoiceInput["groupBy"], order: InvoiceInput["timesOrder"]) {
  const sorted = [...ts].sort((a, b) => (order === "asc" ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date)) || a.id - b.id);
  if (by === "none") return sorted.map((t) => ({ key: String(t.id), title: t.description || t.project, sub: t.project, date: t.date, ids: [t.id], minutes: t.hours * 60 + t.minutes, rate: t.rate, total: t.total }));
  const out: { key: string; title: string; sub: string; date: string; ids: number[]; minutes: number; rate: number; total: number }[] = [];
  for (const t of sorted) {
    const key = by === "project" ? t.project : t.date;
    let g = out.find((x) => x.key === key);
    if (!g) out.push((g = { key, title: by === "project" ? t.project : "", sub: "", date: t.date, ids: [], minutes: 0, rate: t.rate, total: 0 }));
    g.ids.push(t.id);
    g.minutes += t.hours * 60 + t.minutes;
    g.total = Math.round((g.total + t.total) * 100) / 100;
    if (g.rate !== t.rate) g.rate = 0;
  }
  return out;
}
